import { useLocale } from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType, parseNumber, Validation } from '@e2m-customer-portal/shared';
import clsx from 'clsx';
import { addMinutes, Interval, isBefore, isToday, isWeekend, subHours } from 'date-fns';
import { UseInputFieldRules } from 'hooks/use-input-field-rules/use-input-field-rules';
import React, { MouseEvent, ReactElement, startTransition, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditIcon, ErrorIcon, WarningIcon } from '../../../../../../assets/icons';
import {
  Device,
  EditableMarket,
  ForecastProperty,
  ForecastSlice,
  ForecastTarget,
  Maybe,
  Resolution,
} from '../../../../../../generated/graphql';
import { Popover } from '../../../../../../ui/popover/Popover';
import { Direction } from '../../../../utils';
import { SelectedCell } from '../../ForecastList';
import { SpotPriceResult } from '../../spot-price';
import { SpotPriceLabel } from '../../SpotPriceLabel';
import { SelectHandle } from '../DeviceTable';
import { CellInput } from './CellInput';

type MouseStartPosition = {
  x: number;
  y: number;
};

interface DecoratedCellProps {
  device: Device;
  deadlineDate?: Date;
  slice: ForecastSlice;
  powerLevelRules?: Validation<string | number>;
  rules?: UseInputFieldRules;
  isDstSlice?: boolean;
  spotPrice?: SpotPriceResult;
  latestHistoricalSpotPrice?: Maybe<Date>;
  cellIndex: {
    row: number;
    column: number;
  };
  readOnly?: boolean;
  resetTrigger: boolean;
  editedSlices: Set<string>;
  isHighlighted?: boolean;
  overviewMap: Map<string, (HTMLInputElement | null)[][]>;
  selectHandle?: SelectHandle;
  selectedCells?: SelectedCell[];
  setEditedSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setErroneousSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setRevokeDowntimeInterval: React.Dispatch<React.SetStateAction<Interval | null>>;
  setIsHighlighted?: React.Dispatch<React.SetStateAction<boolean>>;
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, Array<Array<HTMLInputElement | null>>>>>;
  switchFocus: (sourceRow: number, sourceColumn: number, direction?: Direction) => boolean;
  setSelectHandle?: React.Dispatch<React.SetStateAction<SelectHandle>>;
  setSelectedCells?: React.Dispatch<React.SetStateAction<SelectedCell[]>>;
  handleSelectAll?: () => void;
  pasteCopiedCells?: (event: React.KeyboardEvent<HTMLInputElement>, startRow: number, startColumn: number) => void;
  saveChanges: () => void;
  currentForecastProperty?: ForecastProperty;
  isFutureIntraday?: boolean;
  futureIntradayMessage?: string;
  valueOverride?: string;
  className?: string;
}

export function DecoratedCell({
  device,
  deadlineDate,
  slice,
  powerLevelRules,
  rules,
  isDstSlice,
  spotPrice,
  latestHistoricalSpotPrice,
  cellIndex,
  readOnly,
  resetTrigger,
  editedSlices,
  isHighlighted,
  overviewMap,
  selectHandle,
  selectedCells,
  setRevokeDowntimeInterval,
  setIsHighlighted,
  setEditedSlices,
  setErroneousSlices,
  setOverviewMap,
  switchFocus,
  setSelectHandle,
  setSelectedCells,
  handleSelectAll,
  pasteCopiedCells,
  saveChanges,
  currentForecastProperty,
  isFutureIntraday,
  futureIntradayMessage,
  valueOverride,
  className,
}: Readonly<DecoratedCellProps>): ReactElement<DecoratedCellProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const deviceMap = overviewMap.get(device.id);
  if (!deviceMap) throw Error(`Data Error: Table overview for device with id "${device.id}" not found`);
  const isIntradayEditable =
    currentForecastProperty?.target === ForecastTarget.VPP &&
    currentForecastProperty?.editableMarkets.includes(EditableMarket.INTRADAY);

  const isDayAheadOnlyEditable =
    currentForecastProperty?.target === ForecastTarget.VPP &&
    currentForecastProperty?.editableMarkets.includes(EditableMarket.DAY_AHEAD) &&
    !currentForecastProperty?.editableMarkets.includes(EditableMarket.INTRADAY);

  const isAfterDayAheadDeadline = deadlineDate && isBefore(slice.date, deadlineDate);
  const isAfterDowntimeDeadline = isBefore(slice.date, subHours(new Date(), 1));
  const isAfterIntradayDeadline = isBefore(
    slice.date,
    addMinutes(
      new Date(),
      device.deadlines.find((deadline) => deadline.type === InputFieldType.PRODUCTION)?.deadline
        ?.minutesBeforeIntradayDelivery ?? 15,
    ),
  );
  const isAfterDeadline =
    (isIntradayEditable && isAfterIntradayDeadline) || (!isIntradayEditable && isAfterDayAheadDeadline);
  const convertProductionValue = () =>
    convertMeasure(
      slice.fields.find((value) => value.type === InputFieldType.PRODUCTION)?.value ?? {
        value: null,
        unit: 'kW',
      },
      'kW',
    ).value;
  const isAfterVPPDayAheadDeadline = currentForecastProperty?.target === ForecastTarget.VPP && !isAfterDayAheadDeadline;

  const [value, setValue] = useState(
    valueOverride ?? convertProductionValue()?.toLocaleString(locale, { useGrouping: false }) ?? '',
  );
  const [preview, setPreview] = useState<string>();
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSelecting, setIsSelecting] = useState<boolean>(false);

  const [position, setPosition] = useState<DOMRect | undefined>(undefined);

  useEffect(() => {
    const production = convertProductionValue();

    if (setIsHighlighted) setIsHighlighted(false);
    const parsedValue = parseNumber(value);
    setEditedSlices((editedSlices) => {
      const slices = new Set(editedSlices);
      if (!isNaN(parsedValue) && parsedValue !== production) {
        slices.add(slice.id);
      } else if (parsedValue === production || (production === null && value === '')) {
        slices.delete(slice.id);
      }
      return slices;
    });
    setErroneousSlices((erroneousSlices) => {
      const slices = new Set(erroneousSlices);
      if (error && !erroneousSlices.has(slice.id)) {
        slices.add(slice.id);
      } else if (!error && erroneousSlices.has(slice.id)) {
        slices.delete(slice.id);
      }
      return slices;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    setValue(valueOverride ?? convertProductionValue()?.toLocaleString(locale, { useGrouping: false }) ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slice.hasDowntime, slice.fields]);

  useEffect(() => {
    setError(undefined);
    setValue(valueOverride ?? convertProductionValue()?.toLocaleString(locale, { useGrouping: false }) ?? '');
    if (setIsHighlighted) setIsHighlighted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTrigger]);

  useEffect(() => {
    if (!selectHandle || !selectedCells || !setSelectedCells || !setSelectHandle) return;

    if (selectedCells.length) setSelectedCells([]);
    if (isSelecting && position) {
      setSelectHandle({
        active: true,
        cellWidth: position.width,
        cellHeight: position.height,
        column: cellIndex.column,
        row: cellIndex.row,
        targetColumn: cellIndex.column,
        targetRow: cellIndex.row,
        selectionType: 'select',
      });
    } else {
      setSelectHandle({
        ...selectHandle,
        active: false,
      });
      setIsSelecting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  const [cellRef, setCellRef] = useState<HTMLTableCellElement | null>();

  const startFillHandle = useCallback(
    (event: MouseEvent) => {
      if (!setSelectHandle) return;

      event.stopPropagation();

      const boundingRect = cellRef?.getBoundingClientRect();
      if (!boundingRect) return;

      setSelectHandle({
        active: true,
        cellWidth: boundingRect.width,
        cellHeight: boundingRect.height,
        column: cellIndex.column,
        row: cellIndex.row,
        targetColumn: cellIndex.column,
        targetRow: event.detail === 2 ? deviceMap.length - 1 : cellIndex.row, // Double-clicking fills the rest of the day
        selectionType: 'fill',
      });
      event.preventDefault();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cellIndex.column, cellIndex.row, cellRef, deviceMap],
  );

  const startSelectHandle = useCallback(
    (event: MouseEvent) => {
      if (readOnly || isAfterDeadline) return;

      event.preventDefault();
      setIsSelecting(true);

      startTransition(() => {
        const boundingRect = cellRef?.getBoundingClientRect();
        if (!boundingRect) return;

        setPosition(boundingRect);
      });
    },

    [cellIndex.column, cellIndex.row, cellRef, isAfterDeadline, readOnly],
  );

  const updateSelectHandle = useCallback(() => {
    if (!selectHandle || !selectedCells || !setSelectHandle) return;

    setSelectHandle({
      ...selectHandle,
      targetColumn: cellIndex.column,
      targetRow: cellIndex.row,
    });
  }, [selectHandle, selectedCells, setSelectHandle, cellIndex.column, cellIndex.row]);

  const stopSelectHandle = useCallback(() => {
    if (!selectHandle || !selectedCells || !setSelectHandle) return;

    setPosition(undefined);
    setIsSelecting(false);
    if (selectHandle.column !== selectHandle.targetColumn || selectHandle.row !== selectHandle.targetRow) return;

    setSelectHandle((prev) => ({
      ...prev,
      active: false,
      column: 0,
      row: 0,
      targetColumn: 0,
      targetRow: 0,
    }));
  }, [selectHandle, setSelectHandle]);

  const isSelected =
    selectHandle &&
    selectedCells &&
    (selectHandle.active || selectedCells.length) &&
    (selectHandle.row !== selectHandle.targetRow || selectHandle.column !== selectHandle.targetColumn);

  // Select observation
  useEffect(() => {
    if (!selectHandle || !selectedCells) return;

    if (selectHandle.active) {
      cellRef?.addEventListener('mouseenter', updateSelectHandle);
    } else {
      cellRef?.removeEventListener('mouseenter', updateSelectHandle);
    }

    return () => cellRef?.removeEventListener('mouseenter', updateSelectHandle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellRef, selectHandle?.active]);

  // Fill preview
  useEffect(() => {
    if (!selectHandle || !selectedCells) return;

    if (
      selectHandle.selectionType === 'fill' &&
      !(selectHandle.column === cellIndex.column && selectHandle.row === cellIndex.row) &&
      cellIndex.column <= Math.max(selectHandle.column, selectHandle.targetColumn) &&
      cellIndex.column >= Math.min(selectHandle.column, selectHandle.targetColumn) &&
      cellIndex.row <= Math.max(selectHandle.row, selectHandle.targetRow) &&
      cellIndex.row >= Math.min(selectHandle.row, selectHandle.targetRow)
    ) {
      setPreview(deviceMap[selectHandle.row][selectHandle.column]?.value);
    } else {
      setPreview(undefined);
    }
  }, [
    cellIndex.column,
    cellIndex.row,
    deviceMap,
    selectHandle?.column,
    selectHandle?.row,
    selectHandle?.selectionType,
    selectHandle?.targetColumn,
    selectHandle?.targetRow,
  ]);

  // Downtime handling
  const [downtimeRef, setDowntimeRef] = useState<HTMLButtonElement | null>();

  const revokeDowntimeClick = () => {
    if (!isAfterDeadline) {
      setEditedSlices((editedSlices) => new Set(editedSlices).add(slice.id));
      setTimeout(() => switchFocus(cellIndex.row, cellIndex.column));
    } else if (isAfterDeadline && !isAfterDowntimeDeadline) {
      setRevokeDowntimeInterval({
        start: slice.date,
        end: addMinutes(slice.date, currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES ? 15 : 60),
      });
    }
  };

  return (
    <td
      ref={(ref) => setCellRef(ref)}
      className={clsx(
        'group/cell relative box-border border-0 border-b border-solid p-0 text-center',
        cellIndex.row % 4 === 3 ? 'border-blue-grey-100' : 'border-grey-100',
        { 'bg-blue-grey-200/5': isWeekend(slice.date) },
        className,
      )}
      onMouseDown={startSelectHandle}
      onMouseUp={stopSelectHandle}
    >
      <div
        className={clsx(
          'group/downtime relative box-border flex w-full items-center justify-center border-0 border-l border-solid border-grey-100 outline-2 -outline-offset-1',
          {
            'bg-green-50/40': isToday(slice.date),
            'z-20 outline outline-error-500': error,
            'focus-within:z-30 focus-within:outline focus-within:outline-green-200': !readOnly && !isSelected && !error,
          },
        )}
      >
        {slice.hasDowntime && !editedSlices.has(slice.id) ? (
          <>
            <button
              ref={setDowntimeRef}
              className={clsx(
                'm-0 flex w-full items-center justify-center border-none bg-transparent p-0 outline-none',
                isAfterDowntimeDeadline ||
                  readOnly ||
                  (currentForecastProperty?.target === ForecastTarget.VPP && isAfterIntradayDeadline)
                  ? 'opacity-50'
                  : 'cursor-pointer',
              )}
              type="button"
              data-testid={`${device.name}/${slice.date.getDate()}.${slice.date.getHours()}.${slice.date.getMinutes()}`}
              disabled={readOnly || (currentForecastProperty?.target === ForecastTarget.VPP && isAfterIntradayDeadline)}
              onClick={isAfterVPPDayAheadDeadline ? (event) => event.currentTarget.focus() : revokeDowntimeClick}
            >
              {isAfterDeadline || isAfterVPPDayAheadDeadline ? (
                <WarningIcon className="text-black/70" data-testid="warning-icon" />
              ) : (
                <EditIcon className="text-black/70" />
              )}
            </button>
            {slice.hasDowntime && isAfterVPPDayAheadDeadline && (
              <Popover
                baseElement={overviewMap.get(device.id)?.at(cellIndex.row)?.at(cellIndex.column) ?? downtimeRef}
                info={<p className="m-0">{t('DOWNTIME_REQUIRES_INTERVAL_CANCEL')}</p>}
              />
            )}
          </>
        ) : (
          <>
            <CellInput
              deviceId={device.id}
              slice={slice}
              powerLevelRules={powerLevelRules}
              rules={rules}
              disabled={isAfterDeadline}
              readOnly={readOnly}
              cellIndex={cellIndex}
              value={value}
              preview={preview}
              deviceMap={deviceMap}
              selectedCells={selectedCells}
              shouldSyncIntradaySisterCells={(isIntradayEditable && !isAfterDayAheadDeadline) || isDayAheadOnlyEditable}
              setValue={setValue}
              setError={setError}
              setOverviewMap={setOverviewMap}
              switchFocus={switchFocus}
              setSelectHandle={setSelectHandle}
              setSelectedCells={setSelectedCells}
              handleSelectAll={handleSelectAll}
              pasteCopiedCells={pasteCopiedCells}
              saveChanges={saveChanges}
            />
            {((slice.hasDowntime && editedSlices.has(slice.id)) || isFutureIntraday || isDstSlice) && !error && (
              <Popover
                baseElement={overviewMap.get(device.id)?.at(cellIndex.row)?.at(cellIndex.column) ?? downtimeRef}
                info={
                  <>
                    {slice.hasDowntime && <p className="m-0">{t('DOWNTIME_CANCELED')}</p>}
                    {isDstSlice && <p className="m-0">{t('FORECAST_DST_WARNING')}</p>}
                    {isFutureIntraday && futureIntradayMessage && <p className="m-0">{futureIntradayMessage}</p>}
                  </>
                }
              />
            )}
          </>
        )}
      </div>
      {spotPrice?.price !== undefined && (
        <div
          className={clsx([
            'spot pointer-events-none absolute inset-y-0 left-0 flex aspect-[1.75] items-center justify-center -tracking-wide border-0 border-x border-solid border-grey-100 text-xs',
            spotPrice.gradient !== undefined ? `bg-heatmap-${spotPrice.gradient}` : 'bg-grey-200',
            {
              'text-white': spotPrice.gradient !== undefined && (spotPrice.gradient < 1 || spotPrice.gradient > 8),
              'saturate-50': latestHistoricalSpotPrice && isBefore(slice.date, latestHistoricalSpotPrice),
            },
          ])}
        >
          <SpotPriceLabel spotPrice={spotPrice.price} unit="€" locale={locale} />
        </div>
      )}
      <div
        className={clsx(
          'pointer-events-none absolute inset-y-0 right-0 z-20 flex aspect-[1.75] items-center justify-center transition-colors',
          {
            'bg-error-500': error,
          },
        )}
      >
        {error ? (
          <ErrorIcon className="fill-white" />
        ) : (
          slice.hasDowntime &&
          !isAfterVPPDayAheadDeadline &&
          !editedSlices.has(slice.id) &&
          !isAfterDeadline && (
            <WarningIcon
              className="transition-colors group-focus-within/cell:fill-green-200"
              data-testid="warning-icon"
            />
          )
        )}
        {!error && editedSlices.has(slice.id) && (
          <>
            {isHighlighted && (
              <div className="absolute right-2 top-0.5 size-5 animate-ping rounded-full bg-error-50/50" />
            )}
            <EditIcon className="text-black/30 group-focus-within/cell:fill-green-200" />
          </>
        )}
      </div>
      {error && (
        <Popover baseElement={overviewMap.get(device.id)?.at(cellIndex.row)?.at(cellIndex.column)} error={error} />
      )}
      {!selectHandle?.active &&
        !error &&
        selectedCells &&
        selectedCells.length <= 1 &&
        !(slice.hasDowntime && isAfterVPPDayAheadDeadline) && (
          <div
            aria-hidden
            className="absolute -bottom-2.5 -right-2.5 z-30 hidden size-5 cursor-crosshair appearance-none items-center justify-center group-last-of-type/cell:-left-2.5 group-last-of-type/cell:right-auto group-focus-within/cell:flex"
            data-testid={`selectHandle-${cellIndex.row}-${cellIndex.column}`}
            onMouseDown={startFillHandle}
          >
            {!readOnly && (
              <div className="box-border size-2 border border-solid border-white/50 bg-green-200 bg-clip-content" />
            )}
          </div>
        )}
    </td>
  );
}
